/**
 * Köttbutiken: sidfoten (sections/kb-sidfot.liquid).
 *
 * Menykolumnerna är <details>: öppna på dator (rubriken går inte att klicka), stängda dragspel i mobil.
 *
 * Nyhetsbrevet skickas utan sidbyte. Shopifys kundformulär laddar annars om sidan som
 * "?customer_posted=true#KbSidfotNyhetsbrev", vilket såg ut som att man skickades till en annan sida. Svaret läses som
 * HTML och formulärets meddelande (tack eller fel) visas på plats. Kräver Shopify en robotkontroll (/challenge)
 * skickas formuläret på vanligt sätt, och adressen städas efteråt.
 */
class KbSidfot extends HTMLElement {
  #dator = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    this.#anpassa();
    this.#dator.addEventListener('change', this.#anpassa);
    this.addEventListener('click', this.#stoppaPaDator);
    this.addEventListener('submit', this.#skicka);
    this.#stadaAdress();
  }

  disconnectedCallback() {
    this.#dator.removeEventListener('change', this.#anpassa);
    this.removeEventListener('click', this.#stoppaPaDator);
    this.removeEventListener('submit', this.#skicka);
  }

  #anpassa = () => {
    for (const kolumn of this.querySelectorAll('details.kb-sf__kolumn')) {
      kolumn.open = this.#dator.matches;
    }
  };

  /** @param {MouseEvent} event */
  #stoppaPaDator = (event) => {
    if (!this.#dator.matches) return;
    if (event.target instanceof Element && event.target.closest('.kb-sf__kolumnrubrik')) event.preventDefault();
  };

  /** @param {SubmitEvent} event */
  #skicka = async (event) => {
    const formular = event.target;
    if (!(formular instanceof HTMLFormElement) || formular.id !== 'KbSidfotNyhetsbrev') return;
    event.preventDefault();

    const knapp = formular.querySelector('button');
    const falt = formular.querySelector('input[type="email"]');
    knapp?.setAttribute('aria-busy', 'true');
    if (knapp) knapp.disabled = true;

    try {
      const svar = await fetch(formular.action, {
        method: 'POST',
        body: new FormData(formular),
        headers: { Accept: 'text/html' },
      });
      if (!svar.ok || svar.url.includes('/challenge')) throw new Error('kontroll');

      const sida = new DOMParser().parseFromString(await svar.text(), 'text/html');
      const nytt = sida.getElementById('KbSidfotNyhetsbrev');
      const meddelande = nytt?.querySelector('.kb-sf__meddelande:not([hidden])');
      const fel = nytt?.querySelector('input[type="email"][aria-invalid="true"]');
      if (!meddelande && !svar.url.includes('customer_posted=true')) throw new Error('okänt svar');

      this.#visa(formular, meddelande?.textContent?.trim(), Boolean(fel));
      if (!fel && falt) falt.value = '';
    } catch {
      formular.submit();
      return;
    } finally {
      knapp?.removeAttribute('aria-busy');
      if (knapp) knapp.disabled = false;
    }
  };

  /**
   * @param {HTMLFormElement} formular
   * @param {string | undefined} text
   * @param {boolean} fel
   */
  #visa(formular, text, fel) {
    const ruta = formular.querySelector('.kb-sf__meddelande');
    const falt = formular.querySelector('input[type="email"]');
    if (!(ruta instanceof HTMLElement)) return;
    ruta.textContent = text || (fel ? '' : ruta.dataset.tack || '');
    ruta.hidden = !ruta.textContent;
    if (falt) {
      falt.setAttribute('aria-describedby', ruta.id);
      if (fel) falt.setAttribute('aria-invalid', 'true');
      else falt.removeAttribute('aria-invalid');
    }
    ruta.focus({ preventScroll: true });
  }

  /** Efter vanlig sändning: ta bort ?customer_posted=true och #KbSidfotNyhetsbrev ur adressen */
  #stadaAdress() {
    const adress = new URL(window.location.href);
    if (!adress.searchParams.has('customer_posted')) return;
    adress.searchParams.delete('customer_posted');
    if (adress.hash === '#KbSidfotNyhetsbrev') adress.hash = '';
    window.history.replaceState(window.history.state, '', adress);
  }
}

if (!customElements.get('kb-sidfot')) {
  customElements.define('kb-sidfot', KbSidfot);
}
